import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Lock, LogOut } from 'lucide-react';

// Shown in place of the app once an individual student's demo (or paid
// validity) has run out.
//
// This is presentation only. The server refuses the same requests through
// checkIndividualAccess regardless of what the browser does, so removing this
// component would change what the student *sees*, not what they can reach.
// Its job is to explain the 403 rather than leave a screen of failed loads.
export default function DemoExpiredScreen({ access }) {
  const { logout, user } = useAuth();
  const expired = access?.mode === 'expired';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm max-w-md w-full p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
          <Lock className="text-amber-600" size={26} />
        </div>

        <h1 className="text-xl font-bold text-gray-900">
          {expired ? 'Your subscription has ended' : 'Your demo period has ended'}
        </h1>

        <p className="text-gray-500 text-sm mt-2">
          {expired
            ? <>Access to {access?.course_name ? <strong>{access.course_name}</strong> : 'your course'} expired
                {access?.expires_on ? ` on ${new Date(access.expires_on).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}.
                Renew to continue where you left off.</>
            : <>Your free trial{access?.course_name ? <> of <strong>{access.course_name}</strong></> : ''} has finished.
                Complete your payment to unlock full access.</>}
        </p>

        {/* No "Pay now" button by design. Individual students never pay from
            inside the app — the Super Admin issues a Razorpay payment link
            that reaches them by SMS, email or WhatsApp. Putting a checkout
            here would create the second payment path this flow deliberately
            avoids. */}
        <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4 text-left">
          <p className="text-sm font-medium text-gray-800">To continue</p>
          <p className="text-sm text-gray-500 mt-1">
            Use the payment link sent to you, or contact us and we will resend it.
          </p>
          <div className="mt-3 text-sm space-y-1">
            <a href="mailto:info@maximuz.in" className="text-indigo-600 hover:underline block">info@maximuz.in</a>
            <a href="tel:+919944882156" className="text-indigo-600 hover:underline block">+91 99448 82156</a>
          </div>
        </div>

        {user?.full_name && (
          <p className="text-xs text-gray-400 mt-5">Signed in as {user.full_name}</p>
        )}
        <button
          onClick={logout}
          className="mt-3 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </div>
  );
}
